import React from 'react';
import { Layers, TrendingUp, Download, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { ShapeMetricBar } from '../ui/shape-metric-bar';
import { BatchAnalysisData, BatchHistogram } from '../../types/detection';
import { CLASS_COLORS, formatClassName } from '../../constants/detectionClasses';
import { useTheme } from '../../contexts/ThemeContext';

// Stat card color configs
const STAT_CARDS = [
  { key: 'images', color: { r: 16, g: 185, b: 129 } },    // Teal
  { key: 'detections', color: { r: 59, g: 130, b: 246 } }, // Blue
  { key: 'classes', color: { r: 139, g: 92, b: 246 } },    // Purple
  { key: 'avg', color: { r: 245, g: 158, b: 11 } },        // Amber
];

const getStatCardStyle = (color: { r: number; g: number; b: number }, isDark: boolean) => {
  const { r, g, b } = color;
  if (isDark) {
    return {
      background: `linear-gradient(145deg, rgba(${r}, ${g}, ${b}, 0.15) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%)`,
      border: `1px solid rgba(${r}, ${g}, ${b}, 0.3)`,
      boxShadow: `0 0 40px rgba(${r}, ${g}, ${b}, 0.15), inset 0 1px 0 rgba(${r}, ${g}, ${b}, 0.2)`,
    };
  }
  // Light mode: more vibrant colors with stronger gradients and borders
  return {
    background: `linear-gradient(145deg, rgba(${r}, ${g}, ${b}, 0.18) 0%, rgba(${r}, ${g}, ${b}, 0.06) 100%)`,
    border: `1px solid rgba(${r}, ${g}, ${b}, 0.35)`,
    boxShadow: `0 4px 16px rgba(${r}, ${g}, ${b}, 0.15), 0 2px 4px rgba(0, 0, 0, 0.05)`,
  };
};

const getRadialGradient = (color: { r: number; g: number; b: number }, isDark: boolean) => {
  const { r, g, b } = color;
  const opacity = isDark ? 0.1 : 0.12;
  return `radial-gradient(circle, rgba(${r}, ${g}, ${b}, ${opacity}) 0%, transparent 70%)`;
};

interface BatchAnalysisProps {
  batchAnalysis: BatchAnalysisData;
  batchHistograms: Record<string, BatchHistogram>;
  isLoadingHistograms: boolean;
  onExport: () => void;
  // Size filter (rendered inline now)
  sizeFilterComponent?: React.ReactNode;
}

export const BatchAnalysis: React.FC<BatchAnalysisProps> = ({
  batchAnalysis,
  batchHistograms,
  isLoadingHistograms,
  onExport,
  sizeFilterComponent,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <Card className="transition-all duration-300 hover:shadow-lg border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent dark:from-primary/10 dark:to-gray-900/50">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Layers className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Batch Analysis</CardTitle>
              <CardDescription>
                Aggregated metrics across {batchAnalysis.totalImages} image{batchAnalysis.totalImages !== 1 ? 's' : ''}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="default" className="text-base px-4 py-1.5 bg-primary font-semibold">
              {batchAnalysis.totalDetections} Total
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Summary - 4-card grid with gradient/glow effects */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Images Analyzed - Teal */}
          <div
            className="relative overflow-hidden rounded-2xl p-6"
            style={getStatCardStyle(STAT_CARDS[0].color, isDark)}
          >
            <div
              className="absolute -top-1/2 -right-1/2 w-full h-full pointer-events-none"
              style={{ background: getRadialGradient(STAT_CARDS[0].color, isDark) }}
            />
            <p className="text-4xl font-bold font-mono text-foreground mb-2">{batchAnalysis.totalImages}</p>
            <p className="text-sm text-muted-foreground font-medium">Images Analyzed</p>
          </div>

          {/* Total Detections - Blue */}
          <div
            className="relative overflow-hidden rounded-2xl p-6"
            style={getStatCardStyle(STAT_CARDS[1].color, isDark)}
          >
            <div
              className="absolute -top-1/2 -right-1/2 w-full h-full pointer-events-none"
              style={{ background: getRadialGradient(STAT_CARDS[1].color, isDark) }}
            />
            <p className="text-4xl font-bold font-mono text-foreground mb-2">{batchAnalysis.totalDetections.toLocaleString()}</p>
            <p className="text-sm text-muted-foreground font-medium">Total Detections</p>
          </div>

          {/* Classes Detected - Purple */}
          <div
            className="relative overflow-hidden rounded-2xl p-6"
            style={getStatCardStyle(STAT_CARDS[2].color, isDark)}
          >
            <div
              className="absolute -top-1/2 -right-1/2 w-full h-full pointer-events-none"
              style={{ background: getRadialGradient(STAT_CARDS[2].color, isDark) }}
            />
            <p className="text-4xl font-bold font-mono text-foreground mb-2">{Object.keys(batchAnalysis.classCounts).length}</p>
            <p className="text-sm text-muted-foreground font-medium">Classes Detected</p>
          </div>

          {/* Avg per Image - Amber */}
          <div
            className="relative overflow-hidden rounded-2xl p-6"
            style={getStatCardStyle(STAT_CARDS[3].color, isDark)}
          >
            <div
              className="absolute -top-1/2 -right-1/2 w-full h-full pointer-events-none"
              style={{ background: getRadialGradient(STAT_CARDS[3].color, isDark) }}
            />
            <p className="text-4xl font-bold font-mono text-foreground mb-2 truncate">
              {(batchAnalysis.totalDetections / batchAnalysis.totalImages).toFixed(1)}
            </p>
            <p className="text-sm text-muted-foreground font-medium">Avg per Image</p>
          </div>
        </div>

        {/* Size Range Filter - Now inline (rendered from parent) */}
        {sizeFilterComponent}

        {/* Class Distribution */}
        <div>
          <Label className="mb-2 block">Class Distribution</Label>
          {/* Horizontal distribution bar */}
          <div className="flex h-4 rounded-full overflow-hidden bg-muted/30 mb-4">
            {Object.entries(batchAnalysis.classCounts).map(([className, count]) => {
              const percentage = (count / batchAnalysis.totalDetections) * 100;
              return (
                <div
                  key={className}
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: CLASS_COLORS[className] || '#888'
                  }}
                  className="transition-all duration-300 hover:opacity-80"
                  title={`${className}: ${count} (${percentage.toFixed(1)}%)`}
                />
              );
            })}
          </div>
          {/* Glass morphism class cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(batchAnalysis.classCounts).map(([className, count]) => {
              const percentage = ((count / batchAnalysis.totalDetections) * 100).toFixed(1);
              const color = CLASS_COLORS[className] || '#888';
              return (
                <div
                  key={className}
                  className="group rounded-xl p-4 transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    background: isDark
                      ? 'rgba(255, 255, 255, 0.03)'
                      : 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: isDark
                      ? '1px solid rgba(255, 255, 255, 0.08)'
                      : '1px solid rgba(0, 0, 0, 0.06)',
                    boxShadow: isDark
                      ? 'none'
                      : '0 2px 8px rgba(0, 0, 0, 0.04)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 12px 24px ${color}25`;
                    e.currentTarget.style.background = isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.9)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = isDark ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.background = isDark
                      ? 'rgba(255, 255, 255, 0.03)'
                      : 'rgba(255, 255, 255, 0.7)';
                  }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: color,
                        boxShadow: isDark ? `0 0 12px ${color}60` : 'none',
                      }}
                    />
                    <span className="text-sm font-semibold text-foreground truncate">
                      {formatClassName(className)}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-2xl font-bold font-mono text-foreground truncate">
                      {count.toLocaleString()}
                    </span>
                    <span
                      className="text-sm font-medium"
                      style={{ color }}
                    >
                      {percentage}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Separator />

        {/* Per-Class Aggregated Statistics (KEEP CURRENT DESIGN with circular rings) */}
        <div>
          <Label className="mb-4 block">Per-Class Analysis (Aggregated)</Label>
          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(batchAnalysis.statsByClass).map(([className, stats]) => (
              <div
                key={className}
                className="bg-background rounded-lg border overflow-hidden transition-all duration-200 hover:shadow-md"
                style={{ borderTopColor: CLASS_COLORS[className] || '#888', borderTopWidth: '3px' }}
              >
                {/* Card Header */}
                <div className="p-3 bg-muted/30 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: CLASS_COLORS[className] || '#888' }}
                    />
                    <span className="font-medium text-sm">{formatClassName(className)}</span>
                  </div>
                  <Badge variant="outline" className="text-xs font-mono">
                    {stats.count} detections
                  </Badge>
                </div>

                {/* Card Content */}
                <div className="p-4 space-y-4">
                  {/* Size Statistics - 5-column grid (KEEP CURRENT DESIGN) */}
                  {stats.mean_um !== null && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Size Distribution</p>
                      <div className="grid grid-cols-5 gap-1.5">
                        <div className="text-center bg-muted/30 rounded p-1.5">
                          <p className="text-[10px] text-muted-foreground">Mean</p>
                          <p className="text-xs font-bold font-mono">{stats.mean_um?.toFixed(2)} um</p>
                        </div>
                        <div className="text-center bg-muted/30 rounded p-1.5">
                          <p className="text-[10px] text-muted-foreground">Median</p>
                          <p className="text-xs font-bold font-mono">{stats.median_um?.toFixed(2)} um</p>
                        </div>
                        <div className="text-center bg-muted/30 rounded p-1.5">
                          <p className="text-[10px] text-muted-foreground">Std Dev</p>
                          <p className="text-xs font-bold font-mono">{stats.std_um?.toFixed(2)} um</p>
                        </div>
                        <div className="text-center bg-muted/30 rounded p-1.5">
                          <p className="text-[10px] text-muted-foreground">Min</p>
                          <p className="text-xs font-bold font-mono">{stats.min_um?.toFixed(2)} um</p>
                        </div>
                        <div className="text-center bg-muted/30 rounded p-1.5">
                          <p className="text-[10px] text-muted-foreground">Max</p>
                          <p className="text-xs font-bold font-mono">{stats.max_um?.toFixed(2)} um</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Shape Metrics with circular rings (KEEP CURRENT DESIGN) */}
                  {stats.shape_metrics && (
                    <div className="border-t pt-3">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">Shape Characteristics</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <ShapeMetricBar
                          label="Roundness"
                          value={stats.shape_metrics.roundness}
                          color={CLASS_COLORS[className] || '#3b82f6'}
                        />
                        <ShapeMetricBar
                          label="Convexity"
                          value={stats.shape_metrics.convexity}
                          color={CLASS_COLORS[className] || '#3b82f6'}
                        />
                        <ShapeMetricBar
                          label="Aspect Ratio"
                          value={stats.shape_metrics.aspect_ratio}
                          maxValue={2}
                          color={CLASS_COLORS[className] || '#3b82f6'}
                        />
                        <ShapeMetricBar
                          label="Solidity"
                          value={stats.shape_metrics.solidity}
                          color={CLASS_COLORS[className] || '#3b82f6'}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Size Distribution Histograms (KEEP CURRENT DESIGN) */}
        <div>
          <Label className="mb-4 block flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Size Distribution Histograms
          </Label>
          {isLoadingHistograms ? (
            <div className="flex flex-col items-center justify-center p-12 bg-muted/20 rounded-lg border-2 border-dashed">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
              <span className="text-muted-foreground font-medium">Generating histograms...</span>
              <span className="text-xs text-muted-foreground mt-1">This may take a moment</span>
            </div>
          ) : Object.keys(batchHistograms).length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(batchHistograms).map(([className, histData]) => (
                <div
                  key={className}
                  className="bg-background rounded-lg border overflow-hidden"
                  style={{ borderTopColor: CLASS_COLORS[className] || '#888', borderTopWidth: '3px' }}
                >
                  <div className="p-3 bg-muted/30 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: CLASS_COLORS[className] || '#888' }}
                      />
                      <span className="font-medium text-sm">{formatClassName(className)}</span>
                    </div>
                    <Badge variant="outline" className="text-xs font-mono">
                      n={histData.stats.count}
                    </Badge>
                  </div>
                  <div className="p-2">
                    <img
                      src={`data:image/png;base64,${histData.histogram}`}
                      alt={`${className} size distribution`}
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-12 bg-muted/20 rounded-lg border-2 border-dashed">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <TrendingUp className="w-6 h-6 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground font-medium">No histograms yet</p>
              <p className="text-xs text-muted-foreground mt-1">Histograms will appear here after processing images</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
